import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { PdfBrandDialog } from "./hs/PdfBrandDialog";
import { loadPdfBrand, loadClientPdfBrand, invalidatePdfBrand, openHsePdf } from "../lib/pdfBrand";
import { FilePdf, Eye, PencilSimple, Plus } from "@phosphor-icons/react";

const TEAM = "__team";
const NONE = "none";

/* Admin: which PDF branding preset this client's paperwork uses (quotes, invoices, completion reports, shared H&S PDFs, monthly safety e-mails). */
export default function ClientPdfBrandPanel({ clientId, clientName = "" }) {
  const [brand, setBrand] = useState(null);
  const [client, setClient] = useState(null);
  const [dlg, setDlg] = useState(null); // null | { presetId } | { create: true }
  const load = () => { loadPdfBrand().then(setBrand); loadClientPdfBrand(clientId).then(setClient); };
  useEffect(() => { load(); window.addEventListener("pdf-brand-changed", load); return () => window.removeEventListener("pdf-brand-changed", load); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const assign = async (presetId) => {
    try {
      const { data } = await API.put(`/pdf-brand/clients/${clientId}`, { preset_id: presetId === TEAM ? "" : presetId });
      toast.success(data.message); invalidatePdfBrand();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't update the client's branding"); }
  };
  if (!brand || !client) return null;
  const presets = brand.presets || [];
  const teamDefault = presets.find((p) => p.id === brand.default_id);
  const value = client.preset_id || TEAM;
  const inherited = client.inherited_from;
  const own = presets.find((p) => p.id === client.preset_id);
  const summary = client.preset_id === NONE ? "your company branding as-is"
    : own ? `the “${own.name}” preset` : teamDefault ? `the team default (“${teamDefault.name}”)` : "your company branding (no team default set)";
  return (
    <div className="mb-6 border border-border rounded-lg overflow-hidden" data-testid="client-pdf-brand-section">
      <div className="flex items-start justify-between gap-3 p-4 bg-muted/20">
        <div className="flex items-center gap-3">
          <FilePdf size={20} className="text-primary" />
          <div>
            <h2 className="text-sm font-semibold font-[Chivo]">Paperwork branding (PDF preset)</h2>
            <p className="text-[10px] text-muted-foreground mt-0.5">Logo size, header and footer used on every PDF for this client — quotes, invoices, completion reports, shared H&amp;S documents and the monthly safety e-mail — whether downloaded, e-mailed or scheduled.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="shrink-0" onClick={() => openHsePdf(`/pdf-brand/preview.pdf?client_id=${clientId}`, { presetId: client.preset_id === NONE ? "none" : (client.effective_id || "none") })} data-testid="client-pdf-brand-preview"><Eye size={14} className="mr-1" /> Preview</Button>
      </div>
      <div className="p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[260px]">
          <p className="text-xs text-muted-foreground mb-1">Use for this client</p>
          <Select value={value} onValueChange={assign} disabled={!!inherited}>
            <SelectTrigger className="h-9" data-testid="client-pdf-brand-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={TEAM}>Team default{teamDefault ? ` · ${teamDefault.name}` : " · company branding"}</SelectItem>
              <SelectItem value={NONE}>Company branding as-is (no preset)</SelectItem>
              {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {own && !inherited && <Button size="sm" variant="outline" onClick={() => setDlg({ presetId: own.id })} data-testid="client-pdf-brand-edit"><PencilSimple size={14} className="mr-1" /> Edit “{own.name}”</Button>}
        {!inherited && <Button size="sm" variant="outline" onClick={() => setDlg({ create: true })} data-testid="client-pdf-brand-new"><Plus size={14} className="mr-1" /> New preset for this client</Button>}
        <p className="basis-full text-xs text-muted-foreground" data-testid="client-pdf-brand-summary">
          {inherited ? <>Inherits <strong>{client.effective_name || "the main client's choice"}</strong> from <Link to={`/clients/${inherited.id}`} className="text-primary hover:underline">{inherited.name}</Link> — change it there.</>
            : <>This client's PDFs use <strong>{summary}</strong>.{client.preset_id && client.preset_id !== NONE && !own ? " That preset was deleted, so the team default applies." : ""}</>}
        </p>
      </div>
      <PdfBrandDialog open={!!dlg} onClose={() => setDlg(null)} title={`PDF branding for ${clientName || "this client"}`}
        description="Set up how this client's paperwork looks. Saving assigns the preset to this client straight away; it stays available for other clients too."
        initialPresetId={dlg?.presetId || ""} initialName={dlg?.create ? `${clientName || "Client"} branding` : ""}
        onSaved={(p) => (p?.id && p.id !== client.preset_id ? assign(p.id) : undefined)} />
    </div>
  );
}
